import { useSelector } from 'react-redux';
import { RootState } from '../store';

export type UserRole = 'Admin' | 'Editor' | 'Viewer';

export const usePermissions = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const roles = user?.roles || [];

  // Check if user has specific role
  const hasRole = (role: UserRole): boolean => {
    return roles.includes(role);
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (rolesToCheck: UserRole[]): boolean => {
    return rolesToCheck.some(role => roles.includes(role));
  };

  // Computed permissions
  const isAdmin = hasRole('Admin');
  const isEditor = hasRole('Editor');
  const isViewer = hasRole('Viewer');

  // Only Admin can access admin section
  const canAccessAdmin = isAdmin;

  // Admin and Editor can create/edit/delete
  const canWrite = isAdmin || isEditor;

  // Everyone can read, but we include this for clarity
  const canRead = true;

  // Specific permissions
  const canManageApplications = isAdmin; // Only Admin can manage applications
  const canManageTeams = isAdmin; // Only Admin can manage teams
  const canManageBilling = isAdmin; // Only Admin can manage billing
  const canManagePlans = isAdmin; // Only Admin can manage plans (super admin)
  const canManageWhiteLabel = isAdmin; // Only Admin can manage white label branding

  const canCreateDashboards = canWrite;
  const canEditDashboards = canWrite;
  const canDeleteDashboards = canWrite;

  const canCreateAlerts = canWrite;
  const canEditAlerts = canWrite;
  const canDeleteAlerts = canWrite;

  const canCreateSLO = canWrite;
  const canEditSLO = canWrite;
  const canDeleteSLO = canWrite;

  const canCreateFunnels = canWrite;
  const canEditFunnels = canWrite;
  const canDeleteFunnels = canWrite;

  const canCreateStatusPages = canWrite;
  const canEditStatusPages = canWrite;
  const canDeleteStatusPages = canWrite;

  const canUseCloudScan = canWrite; // Cloud scan is a write operation (costs money)

  const canManageUsers = isAdmin; // Only Admin can manage users
  const canManageRoles = isAdmin; // Only Admin can manage roles

  return {
    // Role checks
    isAdmin,
    isEditor,
    isViewer,
    hasRole,
    hasAnyRole,

    // General permissions
    canAccessAdmin,
    canRead,
    canWrite,

    // Settings permissions
    canManageApplications,
    canManageTeams,
    canManageBilling,
    canManagePlans,
    canManageUsers,
    canManageRoles,
    canManageWhiteLabel,

    // Feature permissions
    canCreateDashboards,
    canEditDashboards,
    canDeleteDashboards,

    canCreateAlerts,
    canEditAlerts,
    canDeleteAlerts,

    canCreateSLO,
    canEditSLO,
    canDeleteSLO,

    canCreateFunnels,
    canEditFunnels,
    canDeleteFunnels,

    canCreateStatusPages,
    canEditStatusPages,
    canDeleteStatusPages,

    canUseCloudScan,
  };
};
